import React, { useState, useEffect } from 'react';
import { ccmApi } from '../lib/api';
import { assessCarePlanRevision } from '../lib/ai-revision-guard';

const REVISION_SELECTION_CHAR_LIMIT = 2400;

function sectionForTab(tab) {
  return tab === 'careplan' ? 'careplan' : tab === 'billing' ? 'billing' : 'timelog';
}

function labelForTab(tab) {
  return tab === 'careplan' ? 'care plan' : tab === 'billing' ? 'billing note' : 'time log';
}

function sectionContent(section, draft) {
  if (section === 'careplan') return draft?.careplan?.ai || '';
  if (section === 'billing') return draft?.billing?.summary || '';
  return JSON.stringify(draft?.timelog || []);
}

function selectionRootSelector(tab) {
  if (tab === 'careplan') return '.careplan-document';
  if (tab === 'billing') return '.billing-note-document';
  return '.timelog';
}

function normalizeSelectionText(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function elementForNode(node) {
  if (!node) return null;
  return node.nodeType === 1 ? node : node.parentElement;
}

function rangeIsInside(root, range) {
  const el = elementForNode(range?.commonAncestorContainer);
  return !!(root && el && root.contains(el));
}

function sectionLabelForSelection(range, tab) {
  const el = elementForNode(range?.commonAncestorContainer);
  const section = el?.closest?.('.careplan-document-section, .billing-note-document-section, .doc-section');
  const heading = section?.querySelector?.('.careplan-section-title, .billing-note-section-title, h3, h4')
    ?.textContent
    ?.replace(/\s+/g, ' ')
    ?.trim();
  return heading || labelForTab(tab);
}

function selectionPreview(text) {
  const clean = normalizeSelectionText(text);
  return clean.length > 180 ? `${clean.slice(0, 177)}...` : clean;
}

export function DraftAI({ patient, tab, draft, month, onApplyRevision }) {
  const [thread, setThread] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [applying, setApplying] = useState(null);
  const [selectedSnippet, setSelectedSnippet] = useState(null);

  // Reset thread when patient or tab changes
  useEffect(() => {
    setThread([]); setOpen(false); setInput(''); setApplying(null); setSelectedSnippet(null);
  }, [patient?.id, tab]);

  useEffect(() => {
    let mounted = true;
    function captureRevisionSelection() {
      setTimeout(() => {
        if (!mounted) return;
        const sel = window.getSelection?.();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
        const text = normalizeSelectionText(sel.toString());
        if (text.length < 8) return;
        const range = sel.getRangeAt(0);
        const roots = Array.from(document.querySelectorAll(selectionRootSelector(tab)));
        const root = roots.find(candidate => rangeIsInside(candidate, range));
        if (!root) return;
        const clipped = text.slice(0, REVISION_SELECTION_CHAR_LIMIT);
        setSelectedSnippet({
          text: clipped,
          label: sectionLabelForSelection(range, tab),
          words: text.split(/\s+/).filter(Boolean).length,
          truncated: text.length > REVISION_SELECTION_CHAR_LIMIT,
        });
        setOpen(true);
      }, 0);
    }

    window.addEventListener('mouseup', captureRevisionSelection);
    window.addEventListener('touchend', captureRevisionSelection);
    document.addEventListener('keyup', captureRevisionSelection);
    return () => {
      mounted = false;
      window.removeEventListener('mouseup', captureRevisionSelection);
      window.removeEventListener('touchend', captureRevisionSelection);
      document.removeEventListener('keyup', captureRevisionSelection);
    };
  }, [patient?.id, tab]);

  async function send(text) {
    text = (text || '').trim();
    if (!text || busy) return;
    const selection = selectedSnippet?.text
      ? {
          text: selectedSnippet.text,
          label: selectedSnippet.label,
        }
      : null;
    setThread(prev => [...prev, { role: 'user', text, selection }]);
    setInput('');
    setBusy(true);

    const section = sectionForTab(tab);
    const content = sectionContent(section, draft);
    const canApply = section === 'careplan' &&
      !!content.trim() &&
      !!patient?.id &&
      !!month &&
      typeof onApplyRevision === 'function';
    const mode = canApply ? 'replace' : 'suggest';

    try {
      const res = await ccmApi.reviseAI(section, content, text, {
        mode,
        ...(selection ? { selection } : {}),
      });
      const reply = res.text || '(empty reply)';
      const review = canApply && res.text ? assessCarePlanRevision(content, reply, text) : null;
      setThread(prev => [...prev, review?.canApply
        ? {
            role: 'ai',
            text: 'Proposed care plan revision',
            replacement: reply,
            source: content,
            applied: false,
          }
        : {
            role: 'ai',
            text: review?.reason === 'protected-values'
              ? `AI changed protected clinical values, so Apply is disabled. Review manually.\n\n${reply}`
              : canApply && res.text
                ? `AI returned a partial edit instead of a full care plan replacement:\n\n${reply}`
              : reply,
          }
      ]);
    } catch (e) {
      setThread(prev => [...prev, { role: 'ai', text: `AI revise failed: ${e?.message || 'request failed'}`, error: true }]);
    }
    setBusy(false);
  }

  async function applyProposal(index, message) {
    if (!message?.replacement || applying !== null) return;
    setApplying(index);
    try {
      const current = sectionContent('careplan', draft);
      if (message.source && current !== message.source) {
        throw new Error('Care plan changed; ask AI for a fresh revision.');
      }
      await onApplyRevision?.('careplan', message.replacement);
      setThread(prev => prev.map((m, i) => i === index
        ? { ...m, applied: true, text: 'Applied to care plan draft.' }
        : m
      ));
    } catch (e) {
      setThread(prev => [...prev, { role: 'ai', text: `Apply failed: ${e?.message || 'request failed'}`, error: true }]);
    } finally {
      setApplying(null);
    }
  }

  const suggestions = tab === 'careplan'
    ? ['Shorten the LDL goal', 'Make the review schedule more concise']
    : tab === 'billing'
      ? ['Tighten the billing note to 2 sentences', 'Remove duplicate wording']
      : ['Group entries by week', 'Mark portal entries as auto-AI'];

  const expanded = open || thread.length > 0;
  const sectionLabel = labelForTab(tab);

  return (
    <div className={`draft-ai ${expanded ? 'open' : ''}`}>
      {thread.length > 0 && (
        <div className="dai-thread">
          {thread.map((m, i) => (
            <div key={i} className={`dai-msg ${m.role}`}>
              <div className={`dai-bubble ${m.error ? 'error' : ''} ${m.replacement ? 'proposal' : ''}`}>
                {m.replacement ? (
                  <>
                    <div className="dai-proposal-title">{m.text}</div>
                    <pre className="dai-proposal-text">{m.replacement}</pre>
                    <div className="dai-actions">
                      <button
                        className="dai-apply"
                        disabled={m.applied || applying !== null}
                        onClick={() => applyProposal(i, m)}>
                        {applying === i ? 'Applying...' : m.applied ? 'Applied' : 'Apply'}
                      </button>
                      {!m.applied && (
                        <button
                          className="dai-discard"
                          disabled={applying !== null}
                          onClick={() => setThread(prev => prev.filter((_, idx) => idx !== i))}>
                          Dismiss
                        </button>
                      )}
                    </div>
                  </>
                ) : m.text}
                {!m.replacement && m.selection?.text && (
                  <div className="dai-selection-quote">
                    <span>Selected {m.selection.label || sectionLabel}</span>
                    {selectionPreview(m.selection.text)}
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="dai-msg ai">
              <div className="dai-bubble">
                <span className="dai-typing"><span/><span/><span/></span>
              </div>
            </div>
          )}
        </div>
      )}
      {selectedSnippet?.text && (
        <div className="dai-selection-context">
          <div className="dai-selection-meta">
            <span><strong>Selected text</strong> · {selectedSnippet.label || sectionLabel} · {selectedSnippet.words} words{selectedSnippet.truncated ? ' · clipped' : ''}</span>
            <button
              type="button"
              className="dai-selection-clear"
              onClick={() => setSelectedSnippet(null)}
              aria-label="Clear selected AI revision text">
              Clear
            </button>
          </div>
          <div className="dai-selection-preview">{selectionPreview(selectedSnippet.text)}</div>
        </div>
      )}
      <div className="dai-bar">
        <span className="dai-sparkle" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/>
          </svg>
        </span>
        <input
          placeholder={selectedSnippet?.text
            ? `Ask AI to revise the selected ${sectionLabel} text…`
            : `Ask AI to revise this ${sectionLabel}…`}
          value={input}
          onChange={e => setInput(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={e => { if (e.key === 'Enter') send(input); }}
        />
        {expanded && (
          <button className="dai-close" onClick={() => { setOpen(false); setThread([]); setInput(''); setSelectedSnippet(null); }} title="Close" aria-label="Close AI assistant">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M6 18L18 6"/></svg>
          </button>
        )}
        <button className="dai-send" disabled={!input.trim() || busy} onClick={() => send(input)} title="Send" aria-label="Send AI revision request">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        </button>
      </div>
      {expanded && thread.length === 0 && (
        <div className="dai-suggest">
          <span className="dai-suggest-label">Try:</span>
          {suggestions.map(s => (
            <button key={s} className="dai-chip" onClick={() => send(s)}>{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}
